import * as React from "react";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TRADE_POLICY_BUCKET_METRIC_DEFINITIONS } from "../../../constants/tradePolicyBucketMetricDefinitions";
import {
  useTradePolicyFilterCatalogFields,
  type TradePolicyCatalogSnapshot,
} from "../../../hooks/useTradePolicyFilterCatalogFields";
import type {
  PolicyTreeV2,
  TradePolicyAction,
  TradePolicyCatalogField,
  TradePolicyGroupNode,
} from "../../../types/tradePolicy";
import { isPolicyTreeV2 } from "../../../types/tradePolicy";
import type { ReportStrategyPolicyVersion } from "../../../types/reports";
import { mergedMetricDefinitionMap } from "../../../utils/marketContextMetricDefinitions";
import {
  formatExitPlacementLegsActionLabel,
  legAvailabilityFromPolicyRules,
} from "../../../utils/exitPlacementSimLegs";
import { ExitPlacementPolicyTreeEditor } from "./ExitPlacementPolicyTreeEditor";
import { PolicyGraphCanvas } from "./PolicyGraphCanvas";
import {
  buildPolicyRuleFieldExplanation,
  numericGlossaryHelperForPath,
} from "./policyFieldExplanation";
import { PolicyTreeNodesAccordion } from "./PolicyTreeAccordion";
import {
  countRuleLeaves,
  defaultPolicyRoot,
  extractPolicyAction,
  getPolicyViewDisplayNodes,
  parsePolicyTreeToEditorRoot,
  parsePolicyViewSegments,
  policyTreeV2ConditionNodeIds,
  policyTreeV2RuleDisplayNodes,
  resolvePolicyViewSegmentsCombineLabel,
  type EditorNode,
  type EditorRuleNode,
} from "./policyTreeUtils";
import { PolicyActionField } from "./PolicyActionField";
import {
  POLICY_TREE_EMPTY_TEXT_CLASS,
  POLICY_TREE_ERROR_TEXT_CLASS,
  POLICY_TREE_SEGMENT_CARD_CLASS,
  POLICY_TREE_SEGMENT_SUBTITLE_CLASS,
  POLICY_TREE_SEGMENT_TITLE_CLASS,
} from "./policyTreeTheme";

type PolicyViewMode = "builder" | "graph";

const noop = (): void => undefined;
const noopId = (_id: string): void => undefined;
const noopRulePatch = (_id: string, _patch: Partial<EditorRuleNode>): void => undefined;
const noopDrag = (_id: string, _e: React.DragEvent<HTMLElement>): void => undefined;
const noopGroupDrag = (_id: string, _e: React.DragEvent<HTMLDivElement>): void => undefined;
const noopConnect = (_ruleId: string, _groupId: string): void => undefined;
const noopDetach = (_groupId: string, _nodeId: string): void => undefined;
const noopGroupActionChange = (_id: string, _action: TradePolicyAction): void => undefined;
const READ_ONLY_DEFAULT_GROUP_ACTION: TradePolicyAction = "veto";

function ExitPlacementPolicyViewer({
  policy,
  catalogFields,
  fieldsByPath,
  catalogFieldsForPicker,
  fieldExplanationFor,
}: {
  policy: PolicyTreeV2;
  catalogFields: TradePolicyCatalogField[];
  fieldsByPath: Map<string, TradePolicyCatalogField>;
  catalogFieldsForPicker: TradePolicyCatalogField[];
  fieldExplanationFor: (field: TradePolicyCatalogField) => ReturnType<typeof buildPolicyRuleFieldExplanation>;
}): React.ReactElement {
  const [viewMode, setViewMode] = React.useState<PolicyViewMode>("builder");
  const [collapsedGroupIds, setCollapsedGroupIds] = React.useState<Set<string>>(() => new Set());

  const ruleDisplayNodes = React.useMemo(
    () => policyTreeV2RuleDisplayNodes(policy, fieldsByPath),
    [policy, fieldsByPath]
  );

  const allPolicyNodeIds = React.useMemo(
    () => policyTreeV2ConditionNodeIds(policy, fieldsByPath),
    [policy, fieldsByPath]
  );

  const allPolicyGroupsExpanded = React.useMemo(
    () => allPolicyNodeIds.every((id) => !collapsedGroupIds.has(id)),
    [allPolicyNodeIds, collapsedGroupIds]
  );

  const legsActionLabel = React.useMemo(
    () => formatExitPlacementLegsActionLabel(legAvailabilityFromPolicyRules(policy.rules)),
    [policy.rules]
  );

  if ((policy.rules ?? []).length === 0) {
    return <p className={POLICY_TREE_EMPTY_TEXT_CLASS}>No policy rules in this version.</p>;
  }

  return (
    <div className="flex flex-col gap-3" data-exit-placement-policy-viewer="">
      <PolicyActionField actionLabel={legsActionLabel} />
      <div className="flex flex-nowrap items-center justify-between gap-3">
        <Tabs
          data-trade-log-tabs=""
          value={viewMode}
          onValueChange={(next) => {
            if (next === "builder" || next === "graph") setViewMode(next);
          }}
          className="mb-0 w-fit shrink-0"
        >
          <TabsList className="min-w-max">
            <TabsTrigger value="builder">Builder</TabsTrigger>
            <TabsTrigger value="graph">Graph</TabsTrigger>
          </TabsList>
        </Tabs>
        {viewMode === "builder" ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-font="primary"
            data-market-context-bulk-toggle=""
            className="h-8 min-w-8 shrink-0 px-3 text-[0.75rem] normal-case tracking-normal"
            title={allPolicyGroupsExpanded ? "Collapse all policy items" : "Expand all policy items"}
            aria-label={allPolicyGroupsExpanded ? "Collapse all policy items" : "Expand all policy items"}
            onClick={() =>
              setCollapsedGroupIds(allPolicyGroupsExpanded ? new Set(allPolicyNodeIds) : new Set())
            }
          >
            {allPolicyGroupsExpanded ? "Hide all" : "Show all"}
          </Button>
        ) : null}
      </div>

      <ExitPlacementPolicyTreeEditor
        policy={policy}
        ruleDisplayNodes={ruleDisplayNodes}
        catalogFields={catalogFields}
        catalogFieldsForPicker={catalogFieldsForPicker}
        fieldsByPath={fieldsByPath}
        fieldExplanationFor={fieldExplanationFor}
        policyViewMode={viewMode}
        collapsedGroupIds={collapsedGroupIds}
        setCollapsedGroupIds={setCollapsedGroupIds}
        hideGroupAction
        readOnly
      />
    </div>
  );
}

function PolicySegmentViewer({
  payload,
  catalogFields,
  fieldsByPath,
  catalogFieldsForPicker,
  fieldExplanationFor,
  defaultGroupLabel,
  hideGroupAction = false,
}: {
  payload: unknown;
  catalogFields: TradePolicyCatalogField[];
  fieldsByPath: Map<string, TradePolicyCatalogField>;
  catalogFieldsForPicker: TradePolicyCatalogField[];
  fieldExplanationFor: (field: TradePolicyCatalogField) => ReturnType<typeof buildPolicyRuleFieldExplanation>;
  defaultGroupLabel?: string;
  hideGroupAction?: boolean;
}): React.ReactElement {
  const [viewMode, setViewMode] = React.useState<PolicyViewMode>("builder");
  const [collapsedGroupIds, setCollapsedGroupIds] = React.useState<Set<string>>(() => new Set());

  const policyRoot = React.useMemo(
    () => parsePolicyTreeToEditorRoot(payload, fieldsByPath) ?? defaultPolicyRoot(catalogFields),
    [payload, fieldsByPath, catalogFields]
  );

  const ruleLeaves = React.useMemo(() => countRuleLeaves(policyRoot), [policyRoot]);

  const displayNodes = React.useMemo(
    () => getPolicyViewDisplayNodes(policyRoot),
    [policyRoot]
  );

  const allPolicyNodeIds = React.useMemo(() => {
    const out: string[] = [];
    const walk = (node: EditorNode) => {
      out.push(node.id);
      if (node.kind === "group") {
        for (const child of node.children) walk(child);
      }
    };
    for (const node of displayNodes) walk(node);
    return out;
  }, [displayNodes]);

  const allPolicyGroupsExpanded = React.useMemo(
    () => allPolicyNodeIds.every((id) => !collapsedGroupIds.has(id)),
    [allPolicyNodeIds, collapsedGroupIds]
  );

  const actionLabel = React.useMemo(() => {
    const raw = extractPolicyAction(payload);
    if (raw === "Keep") return "Keep Only";
    return raw;
  }, [payload]);

  if (displayNodes.length === 0) {
    return <p className={POLICY_TREE_EMPTY_TEXT_CLASS}>No policy rules in this version.</p>;
  }

  return (
    <div className="flex flex-col gap-3">
      {actionLabel && !hideGroupAction ? <PolicyActionField actionLabel={actionLabel} /> : null}
      <div className="flex flex-nowrap items-center justify-between gap-3">
        <Tabs
          data-trade-log-tabs=""
          value={viewMode}
          onValueChange={(next) => {
            if (next === "builder" || next === "graph") setViewMode(next);
          }}
          className="mb-0 w-fit shrink-0"
        >
          <TabsList className="min-w-max">
            <TabsTrigger value="builder">Builder</TabsTrigger>
            <TabsTrigger value="graph">Graph</TabsTrigger>
          </TabsList>
        </Tabs>
        {viewMode === "builder" ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-font="primary"
            data-market-context-bulk-toggle=""
            className="h-8 min-w-8 shrink-0 px-3 text-[0.75rem] normal-case tracking-normal"
            title={allPolicyGroupsExpanded ? "Collapse all policy items" : "Expand all policy items"}
            aria-label={allPolicyGroupsExpanded ? "Collapse all policy items" : "Expand all policy items"}
            onClick={() =>
              setCollapsedGroupIds(allPolicyGroupsExpanded ? new Set(allPolicyNodeIds) : new Set())
            }
          >
            {allPolicyGroupsExpanded ? "Hide all" : "Show all"}
          </Button>
        ) : null}
      </div>

      {viewMode === "builder" ? (
        <PolicyTreeNodesAccordion
          nodes={displayNodes}
          catalogFields={catalogFields}
          catalogFieldsForPicker={catalogFieldsForPicker}
          fieldsByPath={fieldsByPath}
          ruleLeaves={ruleLeaves}
          collapsedNodeIds={collapsedGroupIds}
          setCollapsedNodeIds={setCollapsedGroupIds}
          canRemoveGroup={false}
          readOnly
          hideGroupAction={hideGroupAction}
          defaultGroupLabel={defaultGroupLabel}
          onUpdateRule={noopRulePatch}
          onRulePathChange={noopId}
          onRuleOpChange={noopId}
          onGroupOpChange={noopId as (id: string, op: TradePolicyGroupNode["op"]) => void}
          onGroupLabelChange={noopId}
          onGoToSourcePick={noopId}
          onRemove={noopId}
          onAddRule={noopId}
          draggedRuleId={null}
          onRuleDragStart={noopDrag}
          onRuleDragEnd={noop}
          onGroupDragOver={noopGroupDrag}
          onGroupDropRule={noopGroupDrag}
          numericHintForPath={numericGlossaryHelperForPath}
          fieldExplanationFor={fieldExplanationFor}
          defaultGroupAction={READ_ONLY_DEFAULT_GROUP_ACTION}
          onGroupActionChange={noopGroupActionChange}
        />
      ) : (
        <PolicyGraphCanvas
          roots={displayNodes}
          catalogFields={catalogFields}
          catalogFieldsForPicker={catalogFieldsForPicker}
          fieldsByPath={fieldsByPath}
          fieldExplanationFor={fieldExplanationFor}
          readOnly
          hideGroupAction={hideGroupAction}
          defaultGroupLabel={defaultGroupLabel}
          onAddGroupNode={noop}
          onConnectRuleToGroup={noopConnect}
          onDetachConnection={noopDetach}
          onRemoveNode={noopId}
          onUpdateRule={noopRulePatch}
          onRulePathChange={noopId}
          onRuleOpChange={noopId}
          onGroupOpChange={noopId as (id: string, op: TradePolicyGroupNode["op"]) => void}
          onGroupLabelChange={noopId}
          onAddRuleToGroup={noopId}
          defaultGroupAction={READ_ONLY_DEFAULT_GROUP_ACTION}
          onGroupActionChange={noopGroupActionChange}
        />
      )}
    </div>
  );
}

export type PolicySimulatorViewerProps = {
  policyTree: unknown;
  /** Used as the group title when a group has no custom label (e.g. policy version). */
  versionLabel?: string;
  /** Resolve source policy labels for combined bundle segments. */
  policyVersions?: ReportStrategyPolicyVersion[];
  /** Hide Remove/Keep group action controls (Exit Placement SL/TP policies). */
  hideGroupAction?: boolean;
  /** When provided, skips the internal filter-catalog fetch. */
  catalog?: TradePolicyCatalogSnapshot;
  /**
   * When false, never wrap segments in card chrome (workspace sheet alignment).
   * When true (default), card chrome is used for multi/labeled segments only.
   */
  segmentChrome?: boolean;
};

export function PolicySimulatorViewer({
  policyTree,
  versionLabel,
  policyVersions = [],
  hideGroupAction = false,
  catalog: catalogProp,
  segmentChrome = true,
}: PolicySimulatorViewerProps): React.ReactElement {
  const internalCatalog = useTradePolicyFilterCatalogFields(!catalogProp);
  const catalogLoading = catalogProp?.catalogLoading ?? internalCatalog.catalogLoading;
  const catalogError = catalogProp?.catalogError ?? internalCatalog.catalogError;
  const catalogFields = catalogProp?.catalogFields ?? internalCatalog.catalogFields;
  const fieldsByPath = catalogProp?.fieldsByPath ?? internalCatalog.fieldsByPath;

  const catalogFieldsForPicker = catalogFields;

  const fieldExplanationFor = React.useCallback(
    (field: TradePolicyCatalogField) =>
      buildPolicyRuleFieldExplanation(
        field,
        mergedMetricDefinitionMap(TRADE_POLICY_BUCKET_METRIC_DEFINITIONS, null)
      ),
    []
  );

  const segments = React.useMemo(
    () => parsePolicyViewSegments(policyTree, policyVersions),
    [policyTree, policyVersions]
  );

  const segmentsCombineLabel = React.useMemo(
    () => resolvePolicyViewSegmentsCombineLabel(policyTree, segments),
    [policyTree, segments]
  );

  if (catalogLoading) {
    return (
      <div className="flex items-center justify-center gap-3 py-10">
        <Loader2 className="size-4 animate-spin text-[color:var(--brand-tropical-surf)]" aria-hidden />
        <p className={POLICY_TREE_EMPTY_TEXT_CLASS}>Loading policy builder…</p>
      </div>
    );
  }

  if (catalogError) {
    return <p className={POLICY_TREE_ERROR_TEXT_CLASS}>{catalogError}</p>;
  }

  if (isPolicyTreeV2(policyTree)) {
    return (
      <ExitPlacementPolicyViewer
        policy={policyTree}
        catalogFields={catalogFields}
        fieldsByPath={fieldsByPath}
        catalogFieldsForPicker={catalogFieldsForPicker}
        fieldExplanationFor={fieldExplanationFor}
      />
    );
  }

  if (segments.length === 0) {
    return <p className={POLICY_TREE_EMPTY_TEXT_CLASS}>No policy tree available.</p>;
  }

  return (
    <div className="flex flex-col gap-4">
      {segments.map((segment, index) => {
        const hasHeading = Boolean(segment.title?.trim() || segment.subtitle?.trim());
        // Card chrome only when grouping multiple / labeled segments — otherwise
        // nested p-4 misaligns Builder tabs with the sheet/dialog header edges.
        const useSegmentChrome =
          segmentChrome && (segments.length > 1 || hasHeading);
        return (
          <React.Fragment key={segment.key}>
            {index > 0 && segmentsCombineLabel ? (
              <div
                data-policy-view-segment-combine=""
                className="flex items-center justify-center gap-3 py-1"
                role="separator"
                aria-label={`Combined with ${segmentsCombineLabel}`}
              >
                <span className="h-px min-w-8 flex-1 bg-border" aria-hidden />
                <span className="shrink-0 font-[family-name:var(--font-primary)] text-xs font-medium tracking-wide text-[color:var(--brand-tropical-surf)]">
                  {segmentsCombineLabel}
                </span>
                <span className="h-px min-w-8 flex-1 bg-border" aria-hidden />
              </div>
            ) : null}
            <div
              data-policy-view-segment=""
              data-policy-view-segment-chrome={useSegmentChrome ? "" : undefined}
              className={useSegmentChrome ? POLICY_TREE_SEGMENT_CARD_CLASS : "min-w-0"}
            >
              {hasHeading ? (
                <div className="mb-3 flex flex-col gap-1">
                  {segment.title ? (
                    <p className={POLICY_TREE_SEGMENT_TITLE_CLASS}>{segment.title}</p>
                  ) : null}
                  {segment.subtitle ? (
                    <p className={POLICY_TREE_SEGMENT_SUBTITLE_CLASS}>{segment.subtitle}</p>
                  ) : null}
                </div>
              ) : null}
              {segment.kind === "management" && isPolicyTreeV2(segment.payload) ? (
                <ExitPlacementPolicyViewer
                  policy={segment.payload}
                  catalogFields={catalogFields}
                  fieldsByPath={fieldsByPath}
                  catalogFieldsForPicker={catalogFieldsForPicker}
                  fieldExplanationFor={fieldExplanationFor}
                />
              ) : (
                <PolicySegmentViewer
                  payload={segment.payload}
                  catalogFields={catalogFields}
                  fieldsByPath={fieldsByPath}
                  catalogFieldsForPicker={catalogFieldsForPicker}
                  fieldExplanationFor={fieldExplanationFor}
                  defaultGroupLabel={
                    segment.groupLabel?.trim() ||
                    (segments.length === 1 ? versionLabel?.trim() : undefined) ||
                    undefined
                  }
                  hideGroupAction={hideGroupAction}
                />
              )}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}
